/**
 * Admin > Customers tab.
 *
 * Extracted from AdminPage.jsx (Feb 2026 refactor).
 * Owns its own filter state; parent supplies data + callback handlers.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Download, Lock, Wallet, Users, Trash2 } from 'lucide-react';

const CUSTOMER_TIER_TABS = [
  { value: '', label: 'All' },
  { value: 'end_user', label: 'End users' },
  { value: 'builder', label: 'Builders' },
  { value: 'retailer', label: 'Retailers' },
  { value: '__pending', label: 'Pending approval' },
];

const CustomersAdmin = ({
  customers, currentUserId,
  onToggleAdmin, onDelete, onNameChange, onTypeChange, onResetPassword,
  onExport, onOpenOverrides, onApprove, onReject, onFilter,
}) => {
  const [tier, setTier] = useState('');
  const [search, setSearch] = useState('');
  const [buyerType, setBuyerType] = useState('');
  const [inactiveDays, setInactiveDays] = useState(30);
  // "highlight=<uid>" query param — sent by the New User Notification email
  // so the admin lands on the exact signup that needs review.
  const [highlightId, setHighlightId] = useState('');
  const highlightRowRef = useRef(null);
  useEffect(() => {
    try {
      const uid = new URLSearchParams(window.location.search).get('highlight');
      if (uid) setHighlightId(uid);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => {
    if (highlightId && highlightRowRef.current) {
      highlightRowRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightId, customers.length]);

  const applyFilters = (patch = {}) => {
    const t = patch.tier ?? tier;
    const s = patch.search ?? search;
    const bt = patch.buyerType ?? buyerType;
    const id = patch.inactiveDays ?? inactiveDays;
    const filters = {};
    if (t === '__pending') filters.approval = 'pending';
    else if (t) filters.user_type = t;
    if (s) filters.search = s;
    if (bt) {
      filters.buyer_type = bt;
      if (bt === 'inactive') filters.inactive_days = Number(id) || 30;
    }
    onFilter(filters);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          {CUSTOMER_TIER_TABS.map(t => (
            <button
              key={t.value}
              onClick={() => { setTier(t.value); applyFilters({ tier: t.value }); }}
              data-testid={`customers-tier-tab-${t.value || 'all'}`}
              className={`px-3 py-1.5 text-xs font-bold rounded-sm border transition-colors ${
                tier === t.value
                  ? 'bg-[#F46A25] text-white border-[#F46A25]'
                  : 'bg-white text-[#12263A] border-neutral-300 hover:border-[#F46A25]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={onExport} className="text-xs bg-green-600 hover:bg-green-700 text-white font-bold px-3 py-2 rounded-sm flex items-center gap-1"><Download className="w-3 h-3" /> Export Excel</button>
      </div>
      <div className="flex flex-wrap items-end gap-3 mb-4 bg-neutral-50 border border-neutral-200 rounded-sm p-3">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase">Search name / email / company</label>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') applyFilters({ search }); }}
            placeholder="e.g. acme, john@..."
            data-testid="customers-search-input"
            className="w-full border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
          />
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase">Buyer</label>
          <select
            value={buyerType}
            onChange={e => { setBuyerType(e.target.value); applyFilters({ buyerType: e.target.value }); }}
            data-testid="customers-buyer-filter"
            className="border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
          >
            <option value="">Any</option>
            <option value="one_time">1-time buyer</option>
            <option value="repeat">Repeat buyer</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        {buyerType === 'inactive' && (
          <div>
            <label className="block text-[11px] font-semibold text-[#12263A] mb-1 uppercase">Inactive (days)</label>
            <input
              type="number"
              min="1"
              value={inactiveDays}
              onChange={e => setInactiveDays(e.target.value)}
              onBlur={() => applyFilters({ inactiveDays })}
              data-testid="customers-inactive-days"
              className="w-24 border border-neutral-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-[#F46A25]"
            />
          </div>
        )}
        <button onClick={() => applyFilters({})} className="text-xs bg-[#12263A] hover:bg-black text-white font-bold px-3 py-2 rounded-sm">Apply</button>
        <span className="text-xs text-neutral-500 ml-auto">{customers.length} result{customers.length === 1 ? '' : 's'}</span>
      </div>
      <div className="bg-white border border-neutral-200 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-xs uppercase text-neutral-600">
            <tr>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Credit £</th>
              <th className="text-left px-4 py-3">Orders</th>
              <th className="text-left px-4 py-3">Spent £</th>
              <th className="text-left px-4 py-3">Last order</th>
              <th className="text-left px-4 py-3">Admin</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.length === 0 && <tr><td colSpan={8} className="text-center p-8 text-neutral-500">No customers match</td></tr>}
            {customers.map(c => {
              const pending = !c.is_approved && c.requested_user_type;
              return (
                <tr
                  key={c.id}
                  ref={c.id === highlightId ? highlightRowRef : undefined}
                  className={`border-t border-neutral-100 transition-colors ${c.id === highlightId ? 'bg-amber-50 ring-2 ring-amber-300' : 'hover:bg-neutral-50'}`}
                  data-testid={`customer-row-${c.id}`}
                >
                  <td className="px-4 py-3">
                    <Link to={`/admin/customers/${c.id}`} className="font-semibold text-[#12263A] hover:text-[#F46A25]" data-testid={`customer-crm-link-${c.id}`}>{c.name}</Link>
                    <div className="text-[11px] text-neutral-500">{c.email}</div>
                    {c.company_name && <div className="text-[11px] text-neutral-600">🏢 {c.company_name}</div>}
                    {pending && (
                      <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold uppercase bg-amber-100 text-amber-800 px-2 py-0.5 rounded-sm" data-testid={`customer-pending-badge-${c.id}`}>
                        Pending: {c.requested_user_type}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <select value={c.user_type} onChange={e => onTypeChange(c.id, e.target.value)} className="border border-neutral-300 rounded-sm px-2 py-1 text-xs focus:outline-none focus:border-[#F46A25]">
                      <option value="end_user">End user</option>
                      <option value="builder">Builder</option>
                      <option value="retailer">Retailer</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    {Number(c.credit_limit || 0) > 0 ? (
                      <span className="text-xs font-bold text-[#F46A25]" data-testid={`credit-limit-${c.id}`}>£{Number(c.credit_limit).toFixed(2)}</span>
                    ) : (
                      <span className="text-xs text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">{c.order_count}</td>
                  <td className="px-4 py-3 text-xs">£{Number(c.total_spent || 0).toFixed(2)}</td>
                  <td className="px-4 py-3 text-xs text-neutral-600">{c.last_order_at ? new Date(c.last_order_at).toLocaleDateString('en-GB') : '—'}</td>
                  <td className="px-4 py-3">
                    <button
                      disabled={c.id === currentUserId}
                      onClick={() => onToggleAdmin(c.id, c.is_admin)}
                      className={`text-xs font-bold uppercase px-2.5 py-1 rounded-sm ${c.is_admin ? 'bg-[#F46A25] text-white' : 'bg-neutral-200 text-neutral-600'} ${c.id === currentUserId ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-80'}`}
                    >{c.is_admin ? 'Admin' : 'User'}</button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 flex-wrap">
                      {pending && (
                        <>
                          <button
                            onClick={() => onApprove(c.id)}
                            data-testid={`approve-customer-${c.id}`}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white font-bold px-2 py-1 rounded-sm"
                          >Approve</button>
                          <button
                            onClick={() => onReject(c.id)}
                            data-testid={`reject-customer-${c.id}`}
                            className="text-xs bg-red-500 hover:bg-red-600 text-white font-bold px-2 py-1 rounded-sm"
                          >Reject</button>
                        </>
                      )}
                      <Link
                        to={`/admin/customers/${c.id}`}
                        data-testid={`open-crm-${c.id}`}
                        className="text-xs text-[#12263A] hover:bg-orange-50 hover:text-[#F46A25] border border-neutral-300 hover:border-[#F46A25] font-semibold px-2 py-1 rounded-sm flex items-center gap-1"
                        title="Open CRM profile"
                      ><Users className="w-3 h-3" /> CRM</Link>
                      <button
                        onClick={() => onOpenOverrides(c)}
                        data-testid={`open-overrides-${c.id}`}
                        className="text-xs text-[#12263A] hover:bg-orange-50 hover:text-[#F46A25] border border-neutral-300 hover:border-[#F46A25] font-semibold px-2 py-1 rounded-sm flex items-center gap-1"
                        title="Credit & pricing overrides"
                      ><Wallet className="w-3 h-3" /> Pricing</button>
                      <button
                        onClick={() => onResetPassword(c.id, c.email)}
                        className="text-xs text-[#12263A] hover:bg-orange-50 hover:text-[#F46A25] border border-neutral-300 hover:border-[#F46A25] font-semibold px-2 py-1 rounded-sm flex items-center gap-1"
                        title="Reset password"
                      ><Lock className="w-3 h-3" /> Reset</button>
                      <button
                        disabled={c.id === currentUserId}
                        onClick={() => onDelete(c.id)}
                        className={`text-red-600 hover:bg-red-50 p-1.5 rounded ${c.id === currentUserId ? 'opacity-40 cursor-not-allowed' : ''}`}
                        aria-label="Delete"
                      ><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CustomersAdmin;
